/**
 * file-selekt upload layer: accept parsing, type/icon mapping, client-side
 * image downscale and the uploader itself.
 *
 * Uploads go to settings.api + endpoint with the g.fetch multipart
 * conventions (data JSON field + the file, X-Api-Token, cookies), but through
 * XMLHttpRequest because fetch() has no upload progress events. The response
 * is the standard envelope; data = the file jsonb (see docs/libs/file-selekt.md).
 */

// One accept vocabulary: type names expand to extensions, anything else is an
// extension. 'image,pdf' or 'jpg,png' or 'image' all work.
export const TYPE_MAP = {
	image: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'svg', 'bmp'],
	video: ['mp4', 'webm', 'mov', 'avi', 'mkv', 'm4v'],
	audio: ['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac'],
	document: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'md', 'odt', 'ods'],
	archive: ['zip', 'rar', '7z', 'tar', 'gz'],
};

export const typeOf = (ext) => Object.keys(TYPE_MAP).find((t) => TYPE_MAP[t].includes(ext)) || 'other';

export const extOf = (name) => {
	const m = String(name || '').match(/\.([a-z0-9]+)$/i);
	return m ? m[1].toLowerCase() : '';
};

const ICONS = {
	image: 'ri-image-line',
	video: 'ri-film-line',
	audio: 'ri-music-2-line',
	document: 'ri-file-text-line',
	archive: 'ri-file-zip-line',
	other: 'ri-file-3-line',
};
const EXT_ICONS = { pdf: 'ri-file-pdf-2-line', doc: 'ri-file-word-line', docx: 'ri-file-word-line', xls: 'ri-file-excel-line', xlsx: 'ri-file-excel-line', ppt: 'ri-file-ppt-line', pptx: 'ri-file-ppt-line', csv: 'ri-file-excel-line' };

export const iconOf = (ext) => EXT_ICONS[ext] || ICONS[typeOf(ext)] || ICONS.other;

// '' = everything allowed. Returns { exts: Set|null, attr: string }.
export function parseAccept(accept) {
	const tokens = String(accept || '').split(',').map((s) => s.trim().toLowerCase().replace(/^\./, '')).filter(Boolean);
	if (!tokens.length) return { exts: null, attr: '' };
	const exts = new Set();
	tokens.forEach((token) => {
		if (TYPE_MAP[token]) TYPE_MAP[token].forEach((e) => exts.add(e));
		else exts.add(token);
	});
	return { exts, attr: [...exts].map((e) => '.' + e).join(',') };
}

export const humanSize = (bytes) => {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Downscale images before upload (resize = max px on the long edge; 0 = off).
// gif/svg pass through (animation/vector would be destroyed).
export async function maybeDownscale(file, maxDim) {
	const ext = extOf(file.name);
	if (!maxDim || typeOf(ext) !== 'image' || ext === 'gif' || ext === 'svg') return file;
	try {
		const bitmap = await createImageBitmap(file);
		const long = Math.max(bitmap.width, bitmap.height);
		if (long <= maxDim) { bitmap.close(); return file; }
		const scale = maxDim / long;
		const canvas = document.createElement('canvas');
		canvas.width = Math.round(bitmap.width * scale);
		canvas.height = Math.round(bitmap.height * scale);
		canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
		bitmap.close();
		const mime = ext === 'png' ? 'image/png' : 'image/jpeg';
		const blob = await new Promise((resolve) => canvas.toBlob(resolve, mime, 0.9));
		if (!blob) return file;
		return new File([blob], file.name, { type: mime });
	} catch (err) {
		return file; // undecodable -> upload as-is, the server will judge
	}
}

// Chunked transport (api.md "File uploads"): above CHUNK_THRESHOLD the file
// travels as sequential CHUNK_BYTES slices — progress stays one smooth 0..1,
// a failed slice retries with backoff (and resumes from the server's
// `received` answer), and the final answer is the SAME file jsonb envelope
// as a single-shot upload. Transparent to the component and to forms.
const CHUNK_BYTES = 16 * 1024 * 1024;
const CHUNK_THRESHOLD = 32 * 1024 * 1024;
const CHUNK_TRIES = 4;

// -> Promise<envelope>; never rejects (same contract as g.fetch).
export function uploadFile(endpoint, file, data, onProgress) {
	if (file.size > CHUNK_THRESHOLD) return uploadChunked(endpoint, file, data, onProgress);
	return post(endpoint, file, file.name, data, onProgress);
}

async function uploadChunked(endpoint, file, data, onProgress) {
	const count = Math.ceil(file.size / CHUNK_BYTES);
	let id = null;
	let index = 0;
	let attempts = 0;
	for (;;) {
		const slice = file.slice(index * CHUNK_BYTES, Math.min(file.size, (index + 1) * CHUNK_BYTES));
		const chunk = { index, count, name: file.name || 'file', size: file.size };
		if (id) chunk.id = id;
		const res = await post(endpoint, slice, file.name, { ...(data || {}), _chunk: chunk }, (p) => {
			if (onProgress) onProgress((index * CHUNK_BYTES + p * slice.size) / file.size);
		});
		const failed = !res.success || (res.error && res.error.code);
		if (failed) {
			// 1035 carries the resume point; transport errors retry the same slice
			if (res.error && res.error.code === 1035 && res.data && Number.isInteger(res.data.received)) index = res.data.received;
			if (++attempts >= CHUNK_TRIES) return res;
			await new Promise((r) => setTimeout(r, 800 * attempts));
			continue;
		}
		attempts = 0;
		if (res.data && res.data.uploadId) id = res.data.uploadId;
		index++;
		if (index >= count) return res; // the last slice answered the file jsonb
	}
}

function post(endpoint, blob, filename, data, onProgress) {
	return new Promise((resolve) => {
		const settings = (window.g && window.g.settings) || {};
		const url = String(settings.api || '').replace(/\/+$/, '') + endpoint;
		const xhr = new XMLHttpRequest();
		xhr.open('POST', url);
		xhr.withCredentials = true;
		if (settings.apiToken) xhr.setRequestHeader('X-Api-Token', settings.apiToken);
		const body = new FormData();
		body.append('data', JSON.stringify(data || {}));
		body.append('file', blob, filename || 'file');
		xhr.upload.addEventListener('progress', (e) => {
			if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
		});
		xhr.addEventListener('load', () => {
			try { resolve(JSON.parse(xhr.responseText)); }
			catch (err) { resolve({ success: false, error: { code: 3, message: 'Invalid API response' } }); }
		});
		xhr.addEventListener('error', () => resolve({ success: false, error: { code: 2, message: 'API not responding' } }));
		xhr.addEventListener('abort', () => resolve({ success: false, error: { code: 2, message: 'Upload aborted' } }));
		xhr.send(body);
	});
}

// Display URL for a stored file: variants win over the original. A PUBLIC
// CDN file's path arrives ABSOLUTE (api.md "CDN storage") — passed through,
// never prefixed with filesUrl.
export function displayUrl(fileData, thumbKey) {
	const settings = (window.g && window.g.settings) || {};
	const base = String(settings.filesUrl || '').replace(/\/+$/, '');
	const variant = fileData.variants && (fileData.variants.cropped || fileData.variants[thumbKey || 'thumb']);
	const rel = (variant && variant.path) || fileData.path;
	if (!rel) return '';
	return /^https?:\/\//.test(rel) ? rel : `${base}/${rel}`;
}

// Draw a crop-coords preview into a canvas (rotate-first space, like the
// server will apply it). Display only - we never read pixels back, so
// cross-origin images are fine.
export function drawCropPreview(canvas, img, crop, maxDim = 480) {
	const rotate = ((crop.rotate || 0) % 360 + 360) % 360;
	const W = img.naturalWidth;
	const H = img.naturalHeight;
	const scale = Math.min(1, maxDim / Math.max(crop.width, crop.height));
	canvas.width = Math.max(1, Math.round(crop.width * scale));
	canvas.height = Math.max(1, Math.round(crop.height * scale));
	const ctx = canvas.getContext('2d');
	ctx.save();
	ctx.scale(scale, scale);
	ctx.translate(-crop.x, -crop.y);
	// original -> rotated space (rotate-first contract)
	if (rotate === 90) ctx.transform(0, 1, -1, 0, H, 0);
	else if (rotate === 180) ctx.transform(-1, 0, 0, -1, W, H);
	else if (rotate === 270) ctx.transform(0, -1, 1, 0, 0, W);
	ctx.drawImage(img, 0, 0);
	ctx.restore();
}
